import React, { useEffect, useRef, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { Trash2, Edit3, Plus, LogOut, Upload, Loader2 } from "lucide-react";
import { api, CATEGORIES, imgSrc } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import PodcastManager from "../components/PodcastManager";
import HomeSettings from "../components/HomeSettings";
import ConfessionsManager from "../components/ConfessionsManager";
import RichTextArea from "../components/RichTextArea";

const EMPTY = {
  title: "",
  excerpt: "",
  content: "",
  category: "way-of-life",
  sub_category: "",
  cover_image: "",
  author: "Bon Sang",
  read_time: 5,
  is_featured: false,
};

export default function Admin() {
  const { user, loading, logout } = useAuth();
  const [tab, setTab] = useState("articles");
  const [articles, setArticles] = useState([]);
  const [draft, setDraft] = useState(EMPTY);
  const [editingSlug, setEditingSlug] = useState(null);
  const [msg, setMsg] = useState("");

  const refresh = () => {
    api.get("/articles", { params: { limit: 200 } }).then((r) => setArticles(r.data));
  };

  useEffect(() => {
    if (user) refresh();
  }, [user]);

  if (loading) return <div className="p-10">Chargement…</div>;
  if (!user) return <Navigate to="/login" replace />;

  const save = async (e) => {
    e.preventDefault();
    setMsg("");
    const payload = { ...draft, sub_category: draft.sub_category || null };
    try {
      if (editingSlug) {
        await api.put(`/articles/${editingSlug}`, payload);
        setMsg("Article mis à jour ✓");
      } else {
        await api.post("/articles", payload);
        setMsg("Article publié ✓");
      }
      setDraft(EMPTY);
      setEditingSlug(null);
      refresh();
    } catch (e2) {
      setMsg("Erreur : " + (e2.response?.data?.detail || e2.message));
    }
  };

  const edit = (a) => {
    setDraft({
      title: a.title,
      excerpt: a.excerpt,
      content: a.content,
      category: a.category,
      sub_category: a.sub_category || "",
      cover_image: a.cover_image,
      author: a.author,
      read_time: a.read_time,
      is_featured: a.is_featured,
    });
    setEditingSlug(a.slug);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const remove = async (slug) => {
    if (!window.confirm("Supprimer cet article ?")) return;
    await api.delete(`/articles/${slug}`);
    refresh();
  };

  return (
    <div data-testid="admin-page" className="mx-auto max-w-[1400px] px-5 lg:px-10 py-12">
      <header className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <p className="text-xs uppercase font-bold tracking-widest">Admin</p>
          <h1 className="font-display-black text-5xl sm:text-6xl">Salle de rédac.</h1>
          <p className="text-sm text-black/60 mt-1">Connectée : {user.email}</p>
        </div>
        <button data-testid="logout-btn" onClick={logout} className="brutal-btn brutal-btn-cream">
          <LogOut size={16} /> Déconnexion
        </button>
      </header>

      <div className="flex gap-2 mb-8 flex-wrap" data-testid="admin-tabs" data-admin-v="3">
        <button
          data-testid="tab-home"
          onClick={() => setTab("home")}
          className={`brutal-btn ${tab === "home" ? "brutal-btn-coral" : "brutal-btn-cream"}`}
        >
          Page d&apos;accueil
        </button>
        <button
          data-testid="tab-articles"
          onClick={() => setTab("articles")}
          className={`brutal-btn ${tab === "articles" ? "brutal-btn-coral" : "brutal-btn-cream"}`}
        >
          Articles
        </button>
        <button
          data-testid="tab-podcast"
          onClick={() => setTab("podcast")}
          className={`brutal-btn ${tab === "podcast" ? "brutal-btn-coral" : "brutal-btn-cream"}`}
        >
          Podcast
        </button>
        <button
          data-testid="tab-confessions"
          onClick={() => setTab("confessions")}
          className={`brutal-btn ${tab === "confessions" ? "brutal-btn-coral" : "brutal-btn-cream"}`}
        >
          Confessions
        </button>
      </div>

      {tab === "home" ? (
        <HomeSettings />
      ) : tab === "podcast" ? (
        <PodcastManager />
      ) : tab === "confessions" ? (
        <ConfessionsManager />
      ) : (
        <div className="grid lg:grid-cols-2 gap-10">
        <form
          onSubmit={save}
          className="brutal-card p-6 flex flex-col gap-4"
          style={{ background: "#D9C5FD" }}
          data-testid="article-form"
        >
          <h2 className="font-display text-3xl">
            {editingSlug ? `Éditer · ${editingSlug}` : "Nouveau papier"}
          </h2>
          <Input label="Titre" testid="form-title" value={draft.title} onChange={(v) => setDraft({ ...draft, title: v })} required />
          <Input
            label="Extrait"
            testid="form-excerpt"
            value={draft.excerpt}
            onChange={(v) => setDraft({ ...draft, excerpt: v })}
            required
          />
          <label className="flex flex-col gap-1">
            <span className="text-xs uppercase font-bold tracking-widest">✏️ Contenu de l&apos;article</span>
            <RichTextArea
              testid="form-content"
              value={draft.content}
              onChange={(v) => setDraft({ ...draft, content: v })}
              placeholder={`Clique sur "Grand titre" pour ajouter un titre.\n\nSélectionne un mot puis clique sur "Gras" pour le mettre en gras.\n\nPour aller à la ligne, appuie 2 fois sur Entrée.`}
            />
            <p className="text-xs text-black/60">
              💡 Astuce : <strong>sélectionne un mot ou une phrase</strong> puis clique sur un bouton (Gras, Italique, etc.) pour l&apos;appliquer.
            </p>
          </label>
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="flex flex-col gap-1">
              <span className="text-xs uppercase font-bold tracking-widest">Catégorie</span>
              <select
                data-testid="form-category"
                value={draft.category}
                onChange={(e) => setDraft({ ...draft, category: e.target.value, sub_category: "" })}
                className="border-2 border-black rounded-xl px-3 py-2 bg-white"
              >
                {CATEGORIES.map((c) => (
                  <option key={c.slug} value={c.slug}>
                    {c.label}
                  </option>
                ))}
              </select>
            </label>
            {draft.category === "way-of-life" ? (
              <label className="flex flex-col gap-1">
                <span className="text-xs uppercase font-bold tracking-widest">Sous-catégorie</span>
                <select
                  data-testid="form-subcat"
                  value={draft.sub_category}
                  onChange={(e) => setDraft({ ...draft, sub_category: e.target.value })}
                  className="border-2 border-black rounded-xl px-3 py-2 bg-white"
                >
                  <option value="">— Aucune —</option>
                  <option value="les-premieres-fois">Les Premières Fois</option>
                </select>
              </label>
            ) : (
              <div />
            )}
          </div>
          <CoverImagePicker
            value={draft.cover_image}
            onChange={(v) => setDraft({ ...draft, cover_image: v })}
          />
          <div className="grid sm:grid-cols-3 gap-3">
            <Input label="Auteur" testid="form-author" value={draft.author} onChange={(v) => setDraft({ ...draft, author: v })} />
            <Input
              label="Temps lecture (min)"
              testid="form-readtime"
              type="number"
              value={draft.read_time}
              onChange={(v) => setDraft({ ...draft, read_time: Number(v) || 0 })}
            />
            <label className="flex items-center gap-2 mt-6">
              <input
                data-testid="form-featured"
                type="checkbox"
                checked={draft.is_featured}
                onChange={(e) => setDraft({ ...draft, is_featured: e.target.checked })}
              />
              <span className="text-xs uppercase font-bold tracking-widest">À la une</span>
            </label>
          </div>
          {msg && <p data-testid="form-msg" className="text-sm font-bold">{msg}</p>}
          <div className="flex gap-3">
            <button data-testid="form-submit" type="submit" className="brutal-btn brutal-btn-coral">
              <Plus size={16} /> {editingSlug ? "Mettre à jour" : "Publier"}
            </button>
            {editingSlug && (
              <button
                type="button"
                onClick={() => {
                  setDraft(EMPTY);
                  setEditingSlug(null);
                }}
                className="brutal-btn brutal-btn-cream"
              >
                Annuler
              </button>
            )}
          </div>
        </form>

        <div data-testid="articles-list" className="flex flex-col gap-3">
          <h2 className="font-display text-3xl">Articles ({articles.length})</h2>
          {articles.map((a) => (
            <div key={a.id} className="brutal-card p-4 flex gap-3 items-center">
              <img src={imgSrc(a.cover_image)} alt="" className="w-16 h-16 border-2 border-black rounded-lg object-cover" />
              <div className="flex-1 min-w-0">
                <p className="text-xs uppercase font-bold tracking-widest text-black/60">{a.category}</p>
                <Link to={`/article/${a.slug}`} className="font-display text-xl hover:underline">
                  {a.title}
                </Link>
              </div>
              <button
                data-testid={`edit-${a.slug}`}
                onClick={() => edit(a)}
                className="p-2 border-2 border-black rounded-full hover:bg-[#FFD01E]"
                aria-label="éditer"
              >
                <Edit3 size={16} />
              </button>
              <button
                data-testid={`delete-${a.slug}`}
                onClick={() => remove(a.slug)}
                className="p-2 border-2 border-black rounded-full hover:bg-[#FA6157]"
                aria-label="supprimer"
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>
      </div>
      )}
    </div>
  );
}

function Input({ label, value, onChange, testid, type = "text", placeholder, required }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs uppercase font-bold tracking-widest">{label}</span>
      <input
        data-testid={testid}
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="border-2 border-black rounded-xl px-3 py-2 bg-white focus:outline-none"
        required={required}
      />
    </label>
  );
}

function CoverImagePicker({ value, onChange }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState("");

  const previewSrc = imgSrc(value);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setErr("");
    if (file.size > 8 * 1024 * 1024) {
      setErr("Fichier trop lourd (max 8 Mo)");
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/upload", form);
      onChange(data.url);
    } catch (e2) {
      const d = e2.response?.data?.detail;
      setErr(typeof d === "string" ? d : "Upload échoué");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <span className="text-xs uppercase font-bold tracking-widest">Image cover</span>
      <div className="flex gap-3 items-start">
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          data-testid="form-cover-upload"
          className="brutal-btn brutal-btn-yellow !py-2 !px-4"
          disabled={uploading}
        >
          {uploading ? (
            <>
              <Loader2 size={16} className="animate-spin" /> Envoi…
            </>
          ) : (
            <>
              <Upload size={16} /> Téléverser
            </>
          )}
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/gif"
          onChange={handleFile}
          className="hidden"
          data-testid="form-cover-file-input"
        />
        <div className="flex-1">
          <input
            data-testid="form-cover"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="…ou colle une URL d'image"
            className="border-2 border-black rounded-xl px-3 py-2 bg-white focus:outline-none w-full"
            required
          />
        </div>
      </div>
      {err && <p className="text-xs text-[#FA6157] font-bold">{err}</p>}
      {previewSrc && (
        <div className="mt-2 w-32 h-32 border-2 border-black rounded-xl overflow-hidden">
          <img src={previewSrc} alt="aperçu" className="w-full h-full object-cover" />
        </div>
      )}
    </div>
  );
}
